import axios from 'axios';
import {
  AdminDashboardData,
  EstudianteDashboardData,
  GeneralDashboardData,
  DashboardStats,
  DashboardStatsDTO,
  AnalyticsStatsDTO,
  HistorialExamenDTO,
  ProgresoEspecialidadDTO,
  ProgresoAnalisisDTO,
  ComparativaDTO,
  RankingDTO
} from '../types/dashboard';

const API_BASE = '/api/dashboard';

// Los interceptors de axios están configurados globalmente en services/index.ts

class DashboardService {
  // ==========================================================
  // DASHBOARDS ESPECÍFICOS POR ROL
  // ==========================================================

  async getAdminDashboard(): Promise<AdminDashboardData> {
    try {
      const response = await axios.get<AdminDashboardData>(`${API_BASE}/admin`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getEstudianteDashboard(): Promise<EstudianteDashboardData> {
    try {
      const response = await axios.get<EstudianteDashboardData>(`${API_BASE}/estudiante`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getGeneralDashboard(): Promise<GeneralDashboardData> {
    try {
      const response = await axios.get<GeneralDashboardData>(`${API_BASE}/general`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==========================================================
  // ESTADÍSTICAS REALES DEL BACKEND
  // ==========================================================

  // Obtener estadísticas básicas del dashboard del estudiante
  async getEstudianteStats(): Promise<DashboardStatsDTO> {
    try {
      const response = await axios.get<DashboardStatsDTO>(`${API_BASE}/estudiante/stats`);
      
      // Validar que la respuesta tenga la estructura esperada
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Respuesta inválida del servidor');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estadísticas del estudiante:', error);
      throw error;
    }
  }

  // Obtener analytics detallados para la página de estadísticas
  async getAnalytics(): Promise<AnalyticsStatsDTO> {
    try {
      const response = await axios.get<AnalyticsStatsDTO>(`${API_BASE}/estudiante/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo analytics:', error);
      throw error;
    }
  }

  // Obtener historial completo de exámenes
  async getHistorial(): Promise<HistorialExamenDTO> {
    try {
      const response = await axios.get<HistorialExamenDTO>(`${API_BASE}/estudiante/historial`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo historial:', error);
      throw error;
    }
  }

  // Obtener tipos de examen activos
  async getTiposExamenActivos() {
    try {
      const response = await axios.get(`/api/tipos-examen/activos`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo tipos de examen:', error);
      throw error;
    }
  }

  // Obtener historial filtrado
  async getHistorialFiltrado(params: { tipo?: string | null; desde?: string | null; hasta?: string | null; q?: string | null; }) {
    try {
      const qs = new URLSearchParams();
      if (params.tipo) qs.set('tipo', params.tipo);
      if (params.desde) qs.set('desde', params.desde);
      if (params.hasta) qs.set('hasta', params.hasta);
      if (params.q) qs.set('q', params.q);
      const url = `${API_BASE}/estudiante/historial/filtrado${qs.toString() ? `?${qs.toString()}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo historial filtrado:', error);
      throw error;
    }
  }

  // Obtener progreso por especialidad (con temas)
  async getProgresoEspecialidad(): Promise<ProgresoAnalisisDTO> {
    try {
      const response = await axios.get<ProgresoAnalisisDTO>(`${API_BASE}/estudiante/progreso`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo progreso por especialidad:', error);
      throw error;
    }
  }

  // Obtener ranking global (leaderboard) + posición del usuario actual
  async getRanking(limit: number = 50): Promise<RankingDTO> {
    try {
      const response = await axios.get<RankingDTO>(`${API_BASE}/estudiante/ranking?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo ranking:', error);
      throw error;
    }
  }

  // Obtener comparativa de rendimiento (promedio nacional, histórico semanal, ranking).
  // Si se pasa "especialidad", el backend devuelve el promedio ENARM de esa especialidad.
  async getComparativa(especialidad?: string): Promise<ComparativaDTO> {
    try {
      const params = especialidad ? { especialidad } : undefined;
      const response = await axios.get<ComparativaDTO>(`${API_BASE}/estudiante/comparativa`, { params });
      return response.data;
    } catch (error) {
      console.error('Error obteniendo comparativa:', error);
      throw error;
    }
  }

  // Obtener días activos de un mes específico
  async getCalendarioMes(year: number, month: number): Promise<{year: number, month: number, diasActivos: number[], success: boolean}> {
    try {
      const response = await axios.get(`${API_BASE}/estudiante/calendario/${year}/${month}`);
      return response.data;
    } catch (error) {
      console.error('Error obteniendo calendario:', error);
      throw error;
    }
  }

  // ==========================================================
  // ESTADÍSTICAS GENERALES (LEGACY)
  // ==========================================================

  async getStats(): Promise<DashboardStats> {
    try {
      const response = await axios.get<DashboardStats>(`${API_BASE}/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ==========================================================
  // UTILIDADES PARA PROCESAR DATOS REALES
  // ==========================================================

  // Formatear estadísticas del dashboard para UI
  formatDashboardStats(stats: DashboardStatsDTO) {
    return {
      promedioGeneral: Math.round(stats.promedioGeneral * 10) / 10,
      tiempoTotal: stats.tiempoTotalFormatted || this.formatStudyTime((stats.tiempoTotalHoras || 0) * 60),
      posicionRanking: stats.posicionRanking || null,
      totalUsuarios: stats.totalUsuarios || null,
      progresoPorcentaje: stats.examenesCompletados ? Math.min(100, Math.round((stats.examenesCompletados / 20) * 100)) : 0,
      rachaEstudio: 0, // TODO: Implementar racha
      examenesCompletados: stats.examenesCompletados || 0
    };
  }

  // Formatear analytics para página de estadísticas
  formatAnalytics(analytics: AnalyticsStatsDTO) {
    return {
      puntuacionTotal: Math.round((analytics.promedioGeneral || 0) * 10) / 10,
      tiempoTotal: this.formatStudyTime(analytics.tiempoTotalMinutos || 0),
      tiempoSemanal: this.formatStudyTime(analytics.tiempoSemanalMinutos || 0),
      tiempoDiarioPromedio: this.formatStudyTime(analytics.tiempoDiarioPromedioMinutos || 0),
      mejorPuntaje: Math.round((analytics.mejorPuntaje || 0) * 10) / 10,
      posicionRanking: analytics.posicionRanking || null,
      totalUsuarios: analytics.totalUsuariosRanking || null,
      rachaEstudio: analytics.rachaEstudio || 0,
      totalExamenes: analytics.totalExamenesCompletados || 0,
      tendencia: analytics.tendenciaProgreso || 'ESTABLE'
    };
  }

  // Formatear historial de exámenes
  formatHistorial(historial: HistorialExamenDTO) {
    const examenes = historial.examenes || historial.historial || [];
    return {
      examenes: examenes.map(examen => ({
        ...examen,
        fechaFormatted: examen.fechaFormatted || (examen.fechaRealizacion || examen.fecha ? this.formatDate(examen.fechaRealizacion || examen.fecha!) : 'N/A'),
        duracionFormatted: examen.duracionFormatted || this.formatStudyTime(examen.duracionMinutos || 0),
        porcentajeAciertos: (examen.totalPreguntas && examen.totalPreguntas > 0) ? Math.round(((examen.respuestasCorrectas || examen.preguntasCorrectas || 0) / examen.totalPreguntas) * 100) : 0,
        tipoFormatted: examen.tipo || (examen.tipoExamen ? this.formatTipoExamen(examen.tipoExamen) : 'N/A')
      })),
      resumenGeneral: {
        totalExamenes: historial.totalCompletados || historial.totalExamenes || 0,
        promedioGeneral: Math.round((historial.promedioGeneral || 0) * 10) / 10,
        mejorResultado: Math.round((historial.mejorResultado || 0) * 10) / 10,
        tiempoTotal: historial.tiempoTotalFormatted || this.formatStudyTime(historial.tiempoTotalMinutos || 0)
      }
    };
  }

  // Formatear progreso por especialidad
  formatProgresoEspecialidad(progreso: ProgresoEspecialidadDTO) {
    return progreso.progresoPorEspecialidad.map(especialidad => ({
      ...especialidad,
      promedioFormatted: Math.round(especialidad.promedioGeneral * 10) / 10,
      porcentajeAciertos: Math.round((especialidad.preguntasCorrectas / especialidad.totalPreguntas) * 100),
      ultimoIntentoFormatted: especialidad.ultimoIntento ? this.formatDate(especialidad.ultimoIntento) : 'Sin intentos',
      colorTendencia: this.getTendenciaColor(especialidad.tendencia),
      progresoColor: this.getProgresoColor(especialidad.promedioGeneral)
    }));
  }

  // ==========================================================
  // UTILIDADES PARA PROCESAR DATOS (LEGACY)
  // ==========================================================

  processAdminStats(data: AdminDashboardData): {
    totalUsers: number;
    activeUsers: number;
    newUsersThisMonth: number;
    totalSimulacros: number;
    averageScore: number;
    conversionRate: number;
  } {
    return {
      totalUsers: data.totalUsuarios || 0,
      activeUsers: data.usuariosActivos || 0,
      newUsersThisMonth: data.nuevosUsuarios || 0,
      totalSimulacros: data.totalSimulacros || 0,
      averageScore: data.promedioGeneral || 0,
      conversionRate: data.tasaConversion || 0
    };
  }

  processEstudianteProgress(data: EstudianteDashboardData): {
    completionPercentage: number;
    strongAreas: string[];
    weakAreas: string[];
    recommendedStudyTime: number;
    nextMilestone: string;
  } {
    const completedSimulacros = data.progreso.simulacrosCompletados || 0;
    const totalSimulacros = data.progreso.totalSimulacros || 1;
    
    return {
      completionPercentage: (completedSimulacros / totalSimulacros) * 100,
      strongAreas: data.areasFortaleza || [],
      weakAreas: data.areasMejora || [],
      recommendedStudyTime: data.objetivos.tiempoEstudioRecomendado || 120, // minutos
      nextMilestone: data.objetivos.proximoObjetivo || 'Completar siguiente simulacro'
    };
  }

  generateStudyRecommendations(studentData: EstudianteDashboardData): {
    priority: 'high' | 'medium' | 'low';
    type: 'review' | 'practice' | 'study';
    specialty: string;
    description: string;
    estimatedTime: number; // en minutos
  }[] {
    const recommendations = [];
    
    // Recomendaciones basadas en áreas débiles
    if (studentData.areasMejora && studentData.areasMejora.length > 0) {
      for (const area of studentData.areasMejora.slice(0, 3)) {
        recommendations.push({
          priority: 'high' as const,
          type: 'study' as const,
          specialty: area,
          description: `Reforzar conocimientos en ${area}`,
          estimatedTime: 60
        });
      }
    }
    
    // Recomendaciones basadas en simulacros recientes
    if (studentData.progreso.ultimoPromedio && studentData.progreso.ultimoPromedio < 70) {
      recommendations.push({
        priority: 'high' as const,
        type: 'review' as const,
        specialty: 'General',
        description: 'Revisar errores del último simulacro',
        estimatedTime: 45
      });
    }
    
    // Recomendaciones para mantener áreas fuertes
    if (studentData.areasFortaleza && studentData.areasFortaleza.length > 0) {
      const strongArea = studentData.areasFortaleza[0];
      recommendations.push({
        priority: 'low' as const,
        type: 'practice' as const,
        specialty: strongArea,
        description: `Mantener nivel en ${strongArea}`,
        estimatedTime: 30
      });
    }
    
    return recommendations;
  }

  // ==========================================================
  // FORMATTERS
  // ==========================================================

  formatScore(score: number): string {
    if (score >= 80) return 'Excelente';
    if (score >= 70) return 'Bueno';
    if (score >= 60) return 'Regular';
    return 'Necesita mejorar';
  }

  formatStudyTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? mins + 'm' : ''}`.trim();
    }
    return `${mins}m`;
  }

  formatPercentage(value: number, total: number): string {
    if (total === 0) return '0%';
    const percentage = (value / total) * 100;
    return `${Math.round(percentage)}%`;
  }

  // Formatear fecha ISO a formato legible
  formatDate(isoDate: string): string {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  // Formatear tipo de examen
  formatTipoExamen(tipo: 'RAPIDO' | 'ENARM' | 'FILTRADO'): string {
    switch (tipo) {
      case 'RAPIDO': return 'Examen Rápido';
      case 'ENARM': return 'Simulacro ENARM';
      case 'FILTRADO': return 'Examen Personalizado';
      default: return tipo;
    }
  }

  // Obtener color basado en tendencia
  getTendenciaColor(tendencia: 'SUBIENDO' | 'BAJANDO' | 'ESTABLE'): string {
    switch (tendencia) {
      case 'SUBIENDO': return '#10b981';
      case 'BAJANDO': return '#ef4444';
      case 'ESTABLE': return '#f59e0b';
      default: return '#94a3b8';
    }
  }

  // Obtener color basado en promedio
  getProgresoColor(promedio: number): string {
    if (promedio >= 90) return '#10b981';
    if (promedio >= 80) return '#0ea5e9';
    if (promedio >= 70) return '#f59e0b';
    return '#ef4444';
  }

  // ==========================================================
  // ANÁLISIS Y PREDICCIONES
  // ==========================================================

  predictExamReadiness(studentData: EstudianteDashboardData): {
    readinessLevel: number; // 0-100
    status: 'not_ready' | 'preparing' | 'almost_ready' | 'ready';
    factors: {
      simulacrosCompleted: number;
      averageScore: number;
      consistencyScore: number;
      coverageScore: number;
    };
    recommendations: string[];
  } {
    const simulacrosCompleted = studentData.progreso.simulacrosCompletados || 0;
    const averageScore = studentData.progreso.promedioGeneral || 0;
    const areasStudied = (studentData.areasFortaleza?.length || 0) + (studentData.areasMejora?.length || 0);
    
    // Factores de evaluación
    const factors = {
      simulacrosCompleted: Math.min(simulacrosCompleted / 20, 1) * 25, // 25% del total
      averageScore: (averageScore / 100) * 35, // 35% del total
      consistencyScore: this.calculateConsistency(studentData) * 20, // 20% del total
      coverageScore: Math.min(areasStudied / 15, 1) * 20 // 20% del total
    };
    
    const readinessLevel = Object.values(factors).reduce((sum, factor) => sum + factor, 0);
    
    let status: 'not_ready' | 'preparing' | 'almost_ready' | 'ready';
    if (readinessLevel >= 85) status = 'ready';
    else if (readinessLevel >= 70) status = 'almost_ready';
    else if (readinessLevel >= 40) status = 'preparing';
    else status = 'not_ready';
    
    const recommendations = this.generateReadinessRecommendations(readinessLevel, factors);
    
    return {
      readinessLevel: Math.round(readinessLevel),
      status,
      factors,
      recommendations
    };
  }

  private calculateConsistency(studentData: EstudianteDashboardData): number {
    // Simulación de cálculo de consistencia basado en variabilidad de puntajes
    // En implementación real, esto vendría del backend con datos históricos
    const baseConsistency = 0.7; // Valor por defecto
    const scoreVariation = studentData.progreso.desviacionEstandar || 15;
    
    // Menos variación = mayor consistencia
    return Math.max(0, Math.min(1, baseConsistency - (scoreVariation / 100)));
  }

  private generateReadinessRecommendations(readinessLevel: number, factors: any): string[] {
    const recommendations = [];
    
    if (factors.simulacrosCompleted < 20) {
      recommendations.push('Completar más simulacros de práctica');
    }
    
    if (factors.averageScore < 25) {
      recommendations.push('Mejorar puntaje promedio con estudio dirigido');
    }
    
    if (factors.consistencyScore < 15) {
      recommendations.push('Trabajar en mantener consistencia en los puntajes');
    }
    
    if (factors.coverageScore < 15) {
      recommendations.push('Estudiar una mayor variedad de especialidades');
    }
    
    if (readinessLevel >= 85) {
      recommendations.push('¡Estás listo para el examen! Mantén el nivel con repasos');
    }
    
    return recommendations;
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;